import { AmendmentReasonCode } from '@app/features/amendments/amendment.model';
import { realmRoles, UserRole } from '@app/features/users/user.model';
import { firstDayOfMonth, frenchDate } from './tour-dates';
import { Tour, TourStep, TourVariant } from './tour.model';

/**
 * Les règles de gestion qu'illustrent les parcours d'avenant sur le temps de travail : celles que
 * leur texte affirme, et seulement elles (RG-parcours-instantane).
 */
const WORKING_TIME_AMENDMENT_RULES = [
  'RG-avenant-points-d-entree',
  'RG-avenant-types-eligibles',
  'RG-avenant-contrat-en-cours',
  'RG-avenant-un-seul-en-cours',
  'RG-avenant-prise-d-effet',
  'RG-avenant-temporaire',
  'RG-avenant-motifs',
  'RG-avenant-nom-de-fichier',
  'RG-avenant-numerotation',
  'RG-avenant-flot-de-validation',
  'RG-avenant-liste-des-contrats',
  'RG-avenant-historique',
];

/**
 * Le premier jour du mois prochain, à Paris : une date d'effet toujours à venir, quel que soit le
 * jour où l'on joue (décision 2026-032/20).
 */
const nextMonthFirstDay = () => frenchDate(firstDayOfMonth(1));

const OPEN_AMENDMENT = 'Ouvrez la saisie d’un avenant depuis la liste.';

/** Un parcours d'avenant sur le temps de travail : ce qui change d'un sens à l'autre. */
interface WorkingTimeChange {
  reason: { code: AmendmentReasonCode; label: string };
  /** Le créneau du lundi, 06:00–10:00 au contrat, et ce qu'il devient. */
  slot: { title: string; endTime: string; text: string };
  hoursWithAmendment: number;
}

/**
 * Les étapes d'un avenant sur le temps de travail, dans l'ordre que l'écran demande : les horaires
 * d'abord, puis le motif qu'ils établissent (RG-avenant-motifs) ; jusqu'à l'historique du contrat,
 * où l'avenant laisse sa trace (décision 2026-032/19).
 */
function workingTimeAmendmentSteps(change: WorkingTimeChange): TourStep[] {
  return [
    {
      anchor: 'contracts-table.establish-amendment',
      title: 'Établir un avenant',
      text:
        'Sur chaque CDI ou CDD A qui court ou va courir, signé ou non, sans avenant en cours, ' +
        'cette icône établit un avenant au contrat. On l’établit aussi depuis « Employé ' +
        'existant », sur le dernier contrat du salarié.',
      whenAbsent: 'Revenez à la liste des contrats.',
      actions: [{ kind: 'click' }],
    },
    {
      anchor: 'amendment.effective-date',
      title: 'La date d’effet',
      text:
        'L’avenant s’applique à partir de cette date, ni avant le début du contrat ni après son ' +
        'terme. Il est à durée indéterminée, sauf à le dire temporaire : il prend fin à une date, ' +
        'et les conditions antérieures reviennent d’elles-mêmes.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'type', value: nextMonthFirstDay }],
    },
    {
      anchor: 'amendment.schedules-tab',
      title: 'Les horaires',
      text:
        'L’onglet Horaires part des conditions en vigueur à la date d’effet : celles du ' +
        'contrat, avec ses avenants signés, sans un avenant temporaire déjà échu.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'click' }],
    },
    {
      anchor: 'amendment.schedules',
      title: change.slot.title,
      text: change.slot.text,
      whenAbsent: 'Ouvrez l’onglet Horaires de l’avenant.',
      actions: [
        { kind: 'type', selector: 'input[formcontrolname="endTime"]', value: change.slot.endTime },
      ],
    },
    {
      anchor: 'amendment.amendment-tab',
      title: 'Retour à l’avenant',
      text: 'Les horaires changés, l’onglet Avenant reçoit le motif de ce changement.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'click' }],
    },
    {
      anchor: 'amendment.reasons',
      title: 'L’objet de l’avenant',
      text:
        `Cochez « ${change.reason.label} ». Ne cochez pas le changement d’horaire : une ` +
        'nouvelle durée redéfinit déjà les horaires. Le motif est vérifié contre les horaires ' +
        'saisis.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'click', selector: `[data-reason="${change.reason.code}"] input` }],
    },
    {
      anchor: 'amendment.summary',
      title: 'Le récapitulatif',
      text:
        `20 h par semaine en vigueur, ${change.hoursWithAmendment} h avec l’avenant. Le nom ` +
        'que portera l’avenant s’affiche aussi. Il dit « Avenant 1 » : le numéro qu’il recevra ' +
        's’il est le prochain mis en signature, attribué à l’acceptation du directeur RH.',
      whenAbsent: OPEN_AMENDMENT,
    },
    {
      anchor: 'amendment.save',
      title: 'Enregistrer',
      text:
        'L’avenant s’enregistre en brouillon. Il suit ensuite le circuit de validation, puis ' +
        'part en signature. Le contrat reste signé pendant toute l’instruction : un refus ramène ' +
        'l’avenant en brouillon, et non le contrat.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'click', selector: 'button' }],
      // Le CDI des données fictives, que le parcours modifie.
      reaches: 'POST /contracts/de300000-0000-4000-8000-000000000001/amendments',
    },
    {
      anchor: 'amendment.close',
      title: 'Fermer la saisie',
      text:
        'La saisie montre l’avenant en brouillon, avec ses gestes. Fermée, elle laisse voir la ' +
        'liste des contrats.',
      whenAbsent: OPEN_AMENDMENT,
      actions: [{ kind: 'click' }],
    },
    {
      anchor: 'contracts-table.amendment-in-progress',
      title: 'L’avenant en cours',
      text:
        'Sous le contrat, une seconde ligne : « Avenant n°1 », sa date d’effet, son circuit, ' +
        'ici en brouillon, et ses gestes. Elle l’ouvre, pour le modifier ou le consulter.',
      whenAbsent: 'Fermez la saisie de l’avenant.',
    },
    {
      anchor: 'contract.history',
      title: 'Ouvrir l’historique',
      text:
        'Le bouton d’historique de la ligne ouvre le fil du contrat, qui réunit l’embauche et ' +
        'ses avenants. « Suivant » n’en garde que les avenants, par le filtre « Avenant ».',
      whenAbsent: 'Revenez à la liste des contrats.',
      actions: [{ kind: 'click' }, { kind: 'click', anchor: 'contract-history.amendment-filter' }],
    },
    {
      anchor: 'contract-history.amendment-entry',
      title: 'L’historique du contrat',
      text:
        'L’établissement de l’avenant n°1 y est daté, avec son auteur et ses motifs. Les gestes ' +
        'de sa validation, puis de sa signature, s’y ajouteront.',
      whenAbsent: 'Ouvrez l’historique du contrat depuis la liste.',
    },
  ];
}

export const WORKING_TIME_INCREASE_TOUR: Tour = {
  id: 'avenant/augmentation-temps-de-travail',
  domain: 'Avenant',
  title: 'Créer un avenant d’augmentation du temps de travail',
  rules: WORKING_TIME_AMENDMENT_RULES,
  startUrl: '/contrats',
  steps: workingTimeAmendmentSteps({
    reason: { code: 'WORKING_TIME_INCREASE', label: 'Augmentation du temps de travail' },
    slot: {
      title: 'Allonger un créneau',
      endTime: '12:00',
      text: 'Le lundi finit désormais à 12 h au lieu de 10 h : la journée passe à 6 h, et le récapitulatif compte la nouvelle durée de la semaine.',
    },
    hoursWithAmendment: 22,
  }),
};

export const WORKING_TIME_DECREASE_TOUR: Tour = {
  id: 'avenant/diminution-temps-de-travail',
  domain: 'Avenant',
  title: 'Créer un avenant de diminution du temps de travail',
  rules: WORKING_TIME_AMENDMENT_RULES,
  startUrl: '/contrats',
  steps: workingTimeAmendmentSteps({
    reason: { code: 'WORKING_TIME_DECREASE', label: 'Diminution du temps de travail' },
    slot: {
      title: 'Raccourcir un créneau',
      endTime: '09:00',
      text: 'Le lundi finit désormais à 9 h au lieu de 10 h : la journée passe à 3 h, et le récapitulatif compte la nouvelle durée de la semaine.',
    },
    hoursWithAmendment: 19,
  }),
};

/** Le catalogue des parcours, dans l'ordre du menu. */
export const TOURS: Tour[] = [WORKING_TIME_INCREASE_TOUR, WORKING_TIME_DECREASE_TOUR];

export function findTour(id: string): Tour | undefined {
  return TOURS.find((tour) => tour.id === id);
}

/** Les parcours rangés par domaine, dans l'ordre du catalogue. */
export function toursByDomain(tours: Tour[] = TOURS): { domain: string; tours: Tour[] }[] {
  const domains = [...new Set(tours.map((tour) => tour.domain))];
  return domains.map((domain) => ({
    domain,
    tours: tours.filter((tour) => tour.domain === domain),
  }));
}

/**
 * Les rôles, parmi ceux qu'on vise, pour qui aucune étape du parcours ne vaut : le parcours ne leur
 * est pas proposé (RG-parcours-catalogue).
 */
export function rolesWithoutSteps(tour: Tour, roles: UserRole[]): UserRole[] {
  return roles.filter((role) => !stepsFor(tour, role).length);
}

/** Les étapes du parcours qu'un rôle voit : celles sans rôles, et celles qui lui sont réservées. */
function stepsFor(tour: Tour, role: UserRole): TourStep[] {
  return tour.steps.filter((step) => !step.roles || step.roles.includes(role));
}

/**
 * Les variantes du parcours : une par suite d'étapes distincte que voient les rôles des fonctions,
 * dans l'ordre de leur premier rôle. Les rôles qui voient les mêmes étapes en partagent une ; un
 * rôle qui n'en voit aucune n'en a pas (décision 2026-032/22).
 */
export function tourVariants(tour: Tour, roles: UserRole[] = realmRoles): TourVariant[] {
  const variants: TourVariant[] = [];
  for (const role of roles) {
    const steps = stepsFor(tour, role);
    if (!steps.length) continue;
    const same = variants.find(
      (variant) =>
        variant.steps.length === steps.length &&
        variant.steps.every((step, index) => step === steps[index]),
    );
    if (same) same.roles.push(role);
    else variants.push({ roles: [role], steps, call: steps.find((step) => step.reaches)?.reaches });
  }
  return variants;
}
